import { assertNever } from '../assertNever.ts'

// The generic half of the portal's REST client: the failure vocabulary every
// integrations-service read shares, the request helper that reduces a response
// to it, and the copy each failure renders as. The per-domain modules
// (webhooks.ts, keys.ts) own their response types and endpoints and delegate
// the mechanics here.
//
// Extracted from webhooks.ts when the credentials screen became the second
// consumer — the envelope, the validator-failure shape, and the challenge
// parsing are properties of the service, not of any one endpoint.

/**
 * Why a union rather than a thrown Error: these are all expected outcomes with
 * different renderings — a missing scope is a configuration problem, a 503 is a
 * Curri outage, and neither should read as "your integration is broken".
 */
export type ApiFailure =
  | { kind: 'unauthenticated' }
  | { kind: 'forbidden'; scope: string }
  | { kind: 'unsupportedVersion'; message: string }
  | { kind: 'invalidRequest'; message: string }
  | { kind: 'unavailable' }
  | { kind: 'network' }
  | { kind: 'unexpected'; status: number; message: string }

export type Resource<T> =
  | { status: 'loading' }
  | { status: 'ready'; data: T }
  | { status: 'failed'; failure: ApiFailure }

interface ApiErrorItem {
  code: string
  message: string
  field?: string
}

/**
 * The legacy partner envelope, kept for one release. The service now answers with
 * RFC 9457 problem documents (INT-426), but a browser can hold this bundle while
 * still talking to an app instance that has not rolled over yet — so both shapes
 * are recognised and the newer one wins. Delete this, `isEnvelope`, and the
 * `ApiErrorItem` above once the service change is fully deployed.
 */
interface ErrorEnvelope {
  data: null
  errors: ApiErrorItem[]
}

/**
 * An RFC 9457 problem document — what every failure on the surface is now.
 * `code` is the stable discriminator to branch on; `detail` is the sentence
 * written for this occurrence; `errors` carries one entry per validation issue,
 * each a JSON Pointer into the request.
 */
interface Problem {
  success: false
  type: string
  title: string
  status: number
  detail: string
  instance: string
  code: string
  requestId?: string
  errors?: { detail: string; pointer: string }[]
}

const isProblem = (body: unknown): body is Problem =>
  typeof body === 'object' &&
  body !== null &&
  (body as Problem).success === false &&
  typeof (body as Problem).code === 'string'

/**
 * The undocumented shape @hono/standard-validator's default hook emits on a
 * query 400. It is not the envelope: `error` is singular, and `data` echoes the
 * submitted query rather than being null.
 */
interface ValidatorFailure {
  success: false
  error: { message?: string; path?: (string | number)[] }[]
}

const isEnvelope = (body: unknown): body is ErrorEnvelope =>
  typeof body === 'object' &&
  body !== null &&
  Array.isArray((body as ErrorEnvelope).errors)

const isValidatorFailure = (body: unknown): body is ValidatorFailure =>
  typeof body === 'object' &&
  body !== null &&
  Array.isArray((body as ValidatorFailure).error)

const readBody = async (response: Response): Promise<unknown> => {
  try {
    return await response.json()
  } catch {
    return null
  }
}

/**
 * Every validation issue in one line, each named by the member it points at. The
 * service reports all of them in a single response, so showing only the first
 * would send someone round the loop once per field.
 */
const describeProblemIssues = (problem: Problem): string =>
  (problem.errors ?? [])
    .map(issue => {
      // A JSON Pointer, so `/authHeader/name` reads as `authHeader.name`.
      const field = issue.pointer.replace(/^\//, '').replace(/\//g, '.')
      return field ? `${field}: ${issue.detail}` : issue.detail
    })
    .join('; ')

const describeIssues = (body: ValidatorFailure): string =>
  body.error
    .map(issue => {
      const field = issue.path?.join('.')
      const message = issue.message ?? 'is invalid'
      return field ? `${field}: ${message}` : message
    })
    .join('; ')

const failureFor = async (
  response: Response,
  fallbackScope: string
): Promise<ApiFailure> => {
  const body = await readBody(response)
  const problem = isProblem(body) ? body : undefined
  const first = isEnvelope(body) ? body.errors[0] : undefined

  switch (response.status) {
    case 401:
      return { kind: 'unauthenticated' }
    case 403:
      // The header names the scope authoritatively; the caller's fallback is
      // for a proxy that strips it.
      return {
        kind: 'forbidden',
        scope:
          /scope="([^"]+)"/.exec(
            response.headers.get('www-authenticate') ?? ''
          )?.[1] ?? fallbackScope,
      }
    case 400: {
      // Branch on the problem `code` rather than on a field name: the version
      // guard is a single-cause failure and carries no pointer to match against.
      if (
        problem?.code === 'version_required' ||
        problem?.code === 'version_unsupported'
      ) {
        return { kind: 'unsupportedVersion', message: problem.detail }
      }
      if (problem?.errors?.length) {
        return {
          kind: 'invalidRequest',
          message: describeProblemIssues(problem),
        }
      }
      if (problem) {
        return { kind: 'invalidRequest', message: problem.detail }
      }
      // Legacy shapes, for the rollout window only.
      if (first?.field === 'Curri-Version') {
        return { kind: 'unsupportedVersion', message: first.message }
      }
      if (isValidatorFailure(body)) {
        return { kind: 'invalidRequest', message: describeIssues(body) }
      }
      return {
        kind: 'invalidRequest',
        message: first?.message ?? 'The request was rejected.',
      }
    }
    case 503:
      return { kind: 'unavailable' }
    default:
      return {
        kind: 'unexpected',
        status: response.status,
        message:
          problem?.detail ??
          first?.message ??
          (typeof (body as { message?: string })?.message === 'string'
            ? (body as { message: string }).message
            : 'Something went wrong.'),
      }
  }
}

/**
 * The human sentence a failure carries, or null when the body is missing or is
 * not one of the shapes above (a proxy error page, an aborted body).
 *
 * For callers that render a message directly rather than going through
 * `ApiFailure` — the ghost-control lookup is the only one today.
 */
export const readFailureDetail = async (
  response: Response
): Promise<string | null> => {
  const body = await readBody(response)
  if (isProblem(body)) return body.detail
  if (isEnvelope(body)) return body.errors[0]?.message ?? null
  return null
}

export type ApiFetch = (path: string, init?: RequestInit) => Promise<Response>

/**
 * Result rather than a throw: every caller here renders the failure, and a
 * try/catch around each fetch would say less than the union does.
 */
export type ApiResult<T> =
  | { ok: true; data: T }
  | { ok: false; failure: ApiFailure }

/**
 * `fallbackScope` names the scope a 403 most plausibly means when the response
 * carries no `WWW-Authenticate` challenge — each domain module passes its own
 * read scope.
 */
export const request = async <T>(
  apiFetch: ApiFetch,
  path: string,
  init: RequestInit,
  fallbackScope: string
): Promise<ApiResult<T>> => {
  let response: Response
  try {
    response = await apiFetch(path, init)
  } catch (error) {
    // An aborted fetch is a superseded render, not a failure to report.
    if (init.signal?.aborted) throw error
    return { ok: false, failure: { kind: 'network' } }
  }

  if (!response.ok) {
    return { ok: false, failure: await failureFor(response, fallbackScope) }
  }

  // `{ success: true, data }` since INT-426; `data` is what every caller wants and
  // the tag is the service's, so it is unwrapped once here rather than per call.
  const body = (await response.json()) as { success?: true; data: T }
  return { ok: true, data: body.data }
}

export interface FailureCopy {
  title: string
  body: string
  /** A missing scope is a configuration problem, not an outage. */
  tone: 'danger' | 'warning' | 'information'
}

/**
 * `subject` is the data the failing screen shows — "webhook history", "API
 * keys" — so the two copy lines that name it stay honest about what the reader
 * cannot see.
 */
export const describeFailure = (
  failure: ApiFailure,
  subject: string
): FailureCopy => {
  switch (failure.kind) {
    case 'unauthenticated':
      return {
        title: 'Your session expired',
        body: `Sign in again to see your ${subject}.`,
        tone: 'warning',
      }
    case 'forbidden':
      return {
        title: `This session cannot read ${subject}`,
        body: `It was issued without the ${failure.scope} scope. Sign out and back in to get a token that includes it — if that does not work, the OAuth client needs ${failure.scope} added to its registration.`,
        tone: 'warning',
      }
    case 'unsupportedVersion':
      return {
        title: 'This page is out of date',
        body: `${failure.message} Reload to pick up the current build.`,
        tone: 'danger',
      }
    case 'invalidRequest':
      return {
        title: 'That request was rejected',
        body: failure.message,
        tone: 'danger',
      }
    case 'unavailable':
      return {
        title: 'Curri could not verify your session',
        body: 'The identity service is unreachable right now. This is not a problem with your account or your endpoint — try again shortly.',
        tone: 'information',
      }
    case 'network':
      return {
        title: 'Could not reach Curri',
        body: 'The request did not complete. Check your connection and try again.',
        tone: 'danger',
      }
    case 'unexpected':
      return {
        title: 'Something went wrong',
        body: `${failure.message} (HTTP ${failure.status})`,
        tone: 'danger',
      }
    default:
      return assertNever(failure, 'failure kind')
  }
}
